import { createSelector } from '@reduxjs/toolkit';
import { PromptDto } from '../../../components/types';
import { StateSchema } from '../../StoreProvider/StoreProvider.types';
import { Slices } from '../slices';
import { promptsAdapter } from './slice';

/**
 * Базовые селекторы EntityAdapter
 */
const promptsSelectors = promptsAdapter.getSelectors<StateSchema>(state => state[Slices.Prompts]);

/**
 * Селектор для получения промпта по ID
 * Используется для получения кешированных промптов из store
 */
export const selectPromptById = promptsSelectors.selectById;

/**
 * Селектор для получения всех ID промптов
 * Используется для проверки наличия промпта в кеше
 */
export const selectPromptIds = promptsSelectors.selectIds;

/**
 * Селектор для проверки наличия промпта в кеше
 * Используется в usePrompt hook для оптимизации запросов
 * @param state - состояние приложения
 * @param promptId - ID промпта для проверки
 * @returns true если промпт есть в кеше
 */
export const selectIsPromptCached = createSelector(
    [selectPromptIds, (state: StateSchema, promptId: number) => promptId],
    (ids, promptId) => ids.includes(promptId),
);

/**
 * Селектор для получения кэша главной ленты по ключу
 */
export const selectFeedCache = (state: StateSchema, key: string) => state[Slices.Prompts].lists.main[key];

/**
 * Cелектор для получения промптов главной ленты
 */
export const selectFeedPrompts = createSelector(
    [(state: StateSchema) => state[Slices.Prompts], (_: StateSchema, key: string) => key],
    (promptsState, key) => {
        const cache = promptsState.lists.main[key];

        if (!cache) {
            return [];
        }

        return cache.promptIds
            .map(id => promptsState.entities[id])
            .filter((prompt): prompt is PromptDto => prompt !== undefined);
    },
);

/**
 * Селектор для получения курсов
 */
export const selectCourses = (state: StateSchema) => state[Slices.Prompts].lists.courses;

/**
 * Селектор для получения челленджей
 */
export const selectChallenges = (state: StateSchema) => state[Slices.Prompts].lists.challenges;

/**
 * Селектор для получения кэша "Мои промпты"
 */
export const selectMyFeedCache = (state: StateSchema) => state[Slices.Prompts].lists.my;

/**
 * Cелектор для получения промптов "Мои промпты"
 */
export const selectMyFeedPrompts = createSelector([(state: StateSchema) => state[Slices.Prompts]], promptsState => {
    const cache = promptsState.lists.my;

    if (!cache) {
        return [];
    }

    return cache.promptIds
        .map(id => promptsState.entities[id])
        .filter((prompt): prompt is PromptDto => prompt !== undefined);
});

/**
 * Селектор для получения кэша ленты пользователя
 */
export const selectUserFeedCache = (state: StateSchema, publicId: string) =>
    state[Slices.Prompts].lists.users[publicId];

/**
 * Cелектор для получения похожих промптов из EntityAdapter
 */
export const selectSimilarPrompts = createSelector(
    [(state: StateSchema) => state, (_: StateSchema, promptId: number) => promptId],
    (state, promptId) => {
        const similarIds = state[Slices.Prompts].lists.similar[promptId] || [];

        return similarIds
            .map(id => promptsSelectors.selectById(state, id))
            .filter((prompt): prompt is PromptDto => prompt !== undefined);
    },
);

/**
 * Cелектор для получения промптов пользователя
 */
export const selectUserFeedPrompts = createSelector(
    [(state: StateSchema) => state[Slices.Prompts], (_: StateSchema, publicId: string) => publicId],
    (promptsState, publicId) => {
        const cache = promptsState.lists.users[publicId];

        if (!cache) {
            return [];
        }

        return cache.promptIds
            .map(id => promptsState.entities[id])
            .filter((prompt): prompt is PromptDto => prompt !== undefined);
    },
);
